import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import {
  Target, Users, Building2, Droplets, ArrowUpRight, ArrowDownRight,
  Calculator, Search, ShieldAlert, CheckCircle2,
  AlertTriangle, AlertCircle, ArrowRight, RefreshCw, Briefcase, ArrowLeft, Layers
} from 'lucide-react';
import PageAnimator from '../../components/ui/PageAnimator';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import CustomSelect from '../../components/ui/CustomSelect';
import MiniLoader from '../../components/MiniLoader';
import { getFamilles } from '../../features/familles/api/familleAPI';
import { getSocietes } from '../../features/familles/api/societeAPI';
import api from '../../api';
import {
  extractClientAssets,
  computeAllocationSummary,
  computeClientDriftStatus,
  computeCabinetAllocationSummary,
  RISK_PROFILES
} from '../../features/allocation/utils/allocationMath';

const ASSET_COLORS = {
  Liquidités: 'var(--hp-info-600)',
  Placements: 'var(--hp-success-600)',
  Immobilier: 'var(--hp-warning-600)',
  Alternatif: 'var(--hp-bronze-500)'
};

export default function AllocationManage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [familles, setFamilles] = useState([]);
  const [societes, setSocietes] = useState([]);

  // Sélection active : "" = Vue d'ensemble cabinet, sinon id du client
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClientType, setSelectedClientType] = useState("famille"); // "famille" | "societe"
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [clientDetailLoading, setClientDetailLoading] = useState(false);

  // Filtres pour la matrice de dérive (Vue Cabinet)
  const [driftFilter, setDriftFilter] = useState("ALL"); // ALL | CRITICAL | MODERATE | OVER_LIQUID | ALIGNED
  const [searchQuery, setSearchQuery] = useState("");

  const formatEuro = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val || 0);

  // 1. Chargement initial des familles et sociétés
  const fetchData = async () => {
    try {
      setLoading(true);
      const [famillesData, societesData] = await Promise.all([
        getFamilles().catch(() => []),
        getSocietes().catch(() => [])
      ]);
      setFamilles(famillesData || []);
      setSocietes(societesData || []);
    } catch (err) {
      console.error("Erreur de chargement des données cabinet :", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 2. Calcul du résumé global du cabinet
  const cabinetSummary = useMemo(() => {
    return computeCabinetAllocationSummary(familles, societes);
  }, [familles, societes]);

  // Options du sélecteur de périmètre (sans aucun emoji)
  const scopeOptions = useMemo(() => [
    { value: "CABINET", label: `Vue d'ensemble du cabinet (${cabinetSummary.totalDossiers} dossiers)` },
    ...familles.map(f => ({
      value: `famille:${f.id}`,
      label: `[Foyer] ${f.nom || f.nomFamille || ''} ${f.reference ? `(${f.reference})` : ''}`.trim()
    })),
    ...societes.map(s => ({
      value: `societe:${s.id}`,
      label: `[Société] ${s.denomination || s.raisonSociale || ''} ${s.formeJuridique ? `(${s.formeJuridique})` : ''}`.trim()
    }))
  ], [cabinetSummary.totalDossiers, familles, societes]);

  // 3. Chargement des détails d'un dossier client spécifique
  useEffect(() => {
    if (!selectedClientId) {
      setSelectedClientData(null);
      return;
    }

    const fetchClientDetails = async () => {
      setClientDetailLoading(true);
      try {
        if (selectedClientType === "societe") {
          const [resSoc, resTree] = await Promise.all([
            api.get(`/societes/${selectedClientId}`).catch(() => null),
            api.get(`/societes/${selectedClientId}/tree`).catch(() => null)
          ]);
          const soc = resSoc?.data?.societe || resSoc?.data || societes.find(s => s.id === selectedClientId);
          setSelectedClientData({ client: soc, tree: resTree?.data || null, type: "societe" });
        } else {
          const [resFam, resTree] = await Promise.all([
            api.get(`/familles/${selectedClientId}`).catch(() => null),
            api.get(`/familles/${selectedClientId}/tree`).catch(() => null)
          ]);
          const fam = resFam?.data || familles.find(f => f.id === selectedClientId);
          setSelectedClientData({ client: fam, tree: resTree?.data || null, type: "famille" });
        }
      } catch (err) {
        console.error("Erreur chargement détails client :", err);
      } finally {
        setClientDetailLoading(false);
      }
    };

    fetchClientDetails();
  }, [selectedClientId, selectedClientType, familles, societes]);

  // 4. Calculs pour le client sélectionné
  const clientAnalysis = useMemo(() => {
    if (!selectedClientData || !selectedClientData.client) return null;
    const { client, tree, type } = selectedClientData;
    const assets = extractClientAssets(client, tree, type);
    const summary = computeAllocationSummary(assets, assets.profilRisque);
    const drift = computeClientDriftStatus(summary, assets.profilRisque);

    const initialAllocation = {
      Liquidités: summary.totalLiquidites,
      Placements: summary.totalPlacements,
      Immobilier: summary.totalImmoBrut,
      Alternatif: summary.totalAlternatif
    };

    const targetConfig = RISK_PROFILES[assets.profilRisque] || RISK_PROFILES.Equilibre;
    const target = targetConfig.target;

    const chartData = [
      { name: "Liquidités", value: summary.totalLiquidites },
      { name: "Placements", value: summary.totalPlacements },
      { name: "Immobilier", value: summary.totalImmoBrut },
      { name: "Alternatif", value: summary.totalAlternatif }
    ].filter(d => d.value > 0);

    const driftBarData = [
      {
        name: "Liquidités",
        Actuel: summary.currentPercentages.liquidites,
        Cible: target.liquidites,
        Drift: summary.gapPercentages.liquidites
      },
      {
        name: "Placements",
        Actuel: summary.currentPercentages.placements,
        Cible: target.placements,
        Drift: summary.gapPercentages.placements
      },
      {
        name: "Immobilier",
        Actuel: summary.currentPercentages.immobilier,
        Cible: target.immobilier,
        Drift: summary.gapPercentages.immobilier
      },
      {
        name: "Alternatif",
        Actuel: summary.currentPercentages.alternatif,
        Cible: target.alternatif,
        Drift: summary.gapPercentages.alternatif
      }
    ];

    return {
      assets,
      summary,
      drift,
      initialAllocation,
      targetConfig,
      chartData,
      driftBarData
    };
  }, [selectedClientData]);

  // 5. Filtrage des dossiers pour la matrice de dérive
  const filteredDossiers = useMemo(() => {
    return cabinetSummary.dossiers.filter(d => {
      // Filtre statut
      if (driftFilter === "CRITICAL" && d.status !== "CRITICAL_DRIFT") return false;
      if (driftFilter === "MODERATE" && d.status !== "MODERATE_DRIFT") return false;
      if (driftFilter === "ALIGNED" && d.status !== "ALIGNED") return false;
      if (driftFilter === "OVER_LIQUID" && !d.tags.some(t => t.code === "OVER_LIQUID")) return false;

      // Filtre texte
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = d.name.toLowerCase().includes(q);
        const matchRef = d.reference ? d.reference.toLowerCase().includes(q) : false;
        return matchName || matchRef;
      }
      return true;
    });
  }, [cabinetSummary.dossiers, driftFilter, searchQuery]);

  // 6. Navigation vers le simulateur d'allocation
  const handleSimulerArbitrage = (dossier) => {
    navigate('/outils/allocation', {
      state: {
        clientId: dossier.id,
        clientType: dossier.type,
        clientName: dossier.name,
        formeJuridique: dossier.rawClient?.formeJuridique,
        fromPilotage: true
      }
    });
  };

  const handleSimulerArbitrageFromDetail = () => {
    if (!selectedClientData) return;
    const client = selectedClientData.client;
    navigate('/outils/allocation', {
      state: {
        clientId: client.id || client._id,
        clientType: selectedClientType,
        clientName: client.denomination || client.raisonSociale || client.nomFamille || client.nom,
        formeJuridique: client.formeJuridique,
        fromPilotage: true
      }
    });
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-workspace">
        <MiniLoader />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 bg-workspace min-h-screen relative pb-24">
      <PageAnimator>
        {/* Header Institutionnel */}
        <div
          className="
            relative z-20
            flex shrink-0 flex-col
            gap-6
            overflow-visible
            workspace-card
            bg-cover bg-center bg-no-repeat
            p-5 sm:p-7
            mb-8
            xl:flex-row xl:items-center xl:justify-between
            rounded-3xl
            border border-workspace
            shadow-hp-md
          "
          style={{ backgroundImage: "url('/assets/backgrounds/castle_bg.png')" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 rounded-3xl bg-overlay"
          />

          <div className="relative z-10 flex min-w-0 shrink-0 flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-secondary text-white shadow-hp-md border border-white/20">
              <Target className="h-7 w-7" />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                  Supervision & Pilotage des Allocations
                </h1>
                <Badge variant="outline" className="hidden sm:inline-flex items-center gap-1.5 text-white border-white/30 bg-white/10 backdrop-blur-md">
                  <ShieldAlert className="w-3.5 h-3.5 text-secondary-300" />
                  Gouvernance Patrimoniale
                </Badge>
              </div>
              <p className="mt-1.5 text-sm text-white/80 max-w-2xl">
                Surveillance de l'adéquation réglementaire, détection des dérives d'allocation et tour de contrôle du cabinet.
              </p>
            </div>
          </div>

          {/* Sélecteur de Périmètre (Vue Cabinet vs Client spécifique) */}
          <div className="relative z-10 flex flex-wrap items-center gap-3">
            {selectedClientId ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedClientId("")}
                className="gap-1.5 text-xs bg-white/10 hover:bg-white/20 border-white/20 text-white backdrop-blur-md"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Vue Cabinet
              </Button>
            ) : null}

            <div className="min-w-[280px]">
              <CustomSelect
                options={scopeOptions}
                value={selectedClientId ? `${selectedClientType}:${selectedClientId}` : "CABINET"}
                onChange={(val) => {
                  if (!val || val === "CABINET") {
                    setSelectedClientId("");
                  } else {
                    const [type, id] = val.split(":");
                    setSelectedClientType(type);
                    setSelectedClientId(id);
                  }
                }}
                className="w-full text-xs"
                triggerClassName="bg-white/10 hover:bg-white/20 text-white border-white/20 rounded-xl px-4 py-2 text-xs font-semibold backdrop-blur-md transition-colors"
                placeholder="Sélectionner un périmètre..."
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              className="gap-1.5 text-xs bg-white/10 hover:bg-white/20 border-white/20 text-white backdrop-blur-md"
              title="Rafraîchir les données"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* ========================================================= */}
        {/* MODE 1 : VUE CABINET (Quand aucun client n'est ciblé)    */}
        {/* ========================================================= */}
        {!selectedClientId ? (
          <div className="space-y-8">
            {/* Grille des 4 KPIs Stratégiques Cabinet */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-subtle uppercase tracking-wider">AUM Global Suivi</span>
                  <div className="w-8 h-8 rounded-xl bg-secondary/10 flex items-center justify-center text-secondary">
                    <Briefcase className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-primary tracking-tight">
                  {formatEuro(cabinetSummary.cabinetAum)}
                </div>
                <p className="mt-1.5 text-xs text-subtle">
                  Agrégation nette sur {cabinetSummary.totalDossiers} dossiers actifs
                </p>
              </div>

              <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-subtle uppercase tracking-wider">Pénétration Alternatifs</span>
                  <div className="w-8 h-8 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-600">
                    <Layers className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-primary tracking-tight">
                  {cabinetSummary.cabinetPercentages.alternatif}%
                </div>
                <p className="mt-1.5 text-xs text-subtle">
                  {formatEuro(cabinetSummary.cabinetAlternatif)} investis en non coté
                </p>
              </div>

              <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-subtle uppercase tracking-wider">Surliquidité Dormante</span>
                  <div className="w-8 h-8 rounded-xl bg-info-500/10 flex items-center justify-center text-info-600">
                    <Droplets className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-primary tracking-tight">
                  {cabinetSummary.overLiquidCount} <span className="text-sm font-normal text-subtle">dossiers</span>
                </div>
                <p className="mt-1.5 text-xs text-subtle">
                  Trésorerie {formatEuro(cabinetSummary.cabinetLiquidites)} ({cabinetSummary.cabinetPercentages.liquidites}% du total)
                </p>
              </div>

              <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-subtle uppercase tracking-wider">Dérives Critiques</span>
                  <div className="w-8 h-8 rounded-xl bg-danger-500/10 flex items-center justify-center text-danger-600">
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-danger tracking-tight">
                  {cabinetSummary.criticalDriftCount} <span className="text-sm font-normal text-subtle">dossiers</span>
                </div>
                <p className="mt-1.5 text-xs text-subtle">
                  Écart d'allocation &gt; 10% par rapport au profil cible
                </p>
              </div>
            </div>

            {/* Répartition Globale du Cabinet */}
            <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
              <h2 className="text-base font-bold text-primary mb-4 flex items-center gap-2">
                <Target className="w-4 h-4 text-secondary" />
                Répartition Globale du Portefeuille Cabinet
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <div className="p-4 rounded-xl bg-info-50/50 dark:bg-info-900/10 border border-info-100 dark:border-info-800/40">
                  <p className="text-xs font-semibold text-info-700 dark:text-info-300 uppercase tracking-wider">Liquidités</p>
                  <p className="text-xl font-bold text-primary mt-1">{cabinetSummary.cabinetPercentages.liquidites}%</p>
                  <p className="text-xs text-subtle mt-0.5">{formatEuro(cabinetSummary.cabinetLiquidites)}</p>
                </div>
                <div className="p-4 rounded-xl bg-success-50/50 dark:bg-success-900/10 border border-success-100 dark:border-success-800/40">
                  <p className="text-xs font-semibold text-success-700 dark:text-success-300 uppercase tracking-wider">Placements Cotés</p>
                  <p className="text-xl font-bold text-primary mt-1">{cabinetSummary.cabinetPercentages.placements}%</p>
                  <p className="text-xs text-subtle mt-0.5">{formatEuro(cabinetSummary.cabinetPlacements)}</p>
                </div>
                <div className="p-4 rounded-xl bg-warning-50/50 dark:bg-warning-900/10 border border-warning-100 dark:border-warning-800/40">
                  <p className="text-xs font-semibold text-warning-700 dark:text-warning-300 uppercase tracking-wider">Immobilier Brut</p>
                  <p className="text-xl font-bold text-primary mt-1">{cabinetSummary.cabinetPercentages.immobilier}%</p>
                  <p className="text-xs text-subtle mt-0.5">{formatEuro(cabinetSummary.cabinetImmobilier)}</p>
                </div>
                <div className="p-4 rounded-xl bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-800/40">
                  <p className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider">Actifs Alternatifs</p>
                  <p className="text-xl font-bold text-primary mt-1">{cabinetSummary.cabinetPercentages.alternatif}%</p>
                  <p className="text-xs text-subtle mt-0.5">{formatEuro(cabinetSummary.cabinetAlternatif)}</p>
                </div>
              </div>

              {/* Barre segmentée visuelle */}
              <div className="w-full h-3 rounded-full overflow-hidden flex bg-surface-muted">
                <div style={{ width: `${cabinetSummary.cabinetPercentages.liquidites}%` }} className="bg-info-500" title="Liquidités" />
                <div style={{ width: `${cabinetSummary.cabinetPercentages.placements}%` }} className="bg-success-500" title="Placements" />
                <div style={{ width: `${cabinetSummary.cabinetPercentages.immobilier}%` }} className="bg-warning-500" title="Immobilier" />
                <div style={{ width: `${cabinetSummary.cabinetPercentages.alternatif}%` }} className="bg-purple-500" title="Alternatifs" />
              </div>
            </div>

            {/* Matrice de Dérive des Dossiers (Drift Matrix) */}
            <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-lg font-bold text-primary flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-secondary" />
                    Matrice de Surveillance & Dérive des Allocations
                  </h2>
                  <p className="text-xs text-subtle mt-1">
                    Identification des décalages par rapport aux profils de risque contractuels et arbitrage direct.
                  </p>
                </div>

                {/* Recherche dans la matrice */}
                <div className="relative min-w-[240px]">
                  <Search className="w-4 h-4 absolute left-3 top-3 text-subtle pointer-events-none z-10" />
                  <Input
                    type="text"
                    placeholder="Rechercher un dossier..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 text-xs h-10 bg-surface-muted/50"
                  />
                </div>
              </div>

              {/* Filtres de Statut */}
              <div className="flex flex-wrap gap-2 mb-6 border-b border-workspace pb-4">
                <Button
                  type="button"
                  size="sm"
                  variant={driftFilter === "ALL" ? "default" : "outline"}
                  onClick={() => setDriftFilter("ALL")}
                  className="gap-1.5 text-xs h-8"
                >
                  <Target className="w-3.5 h-3.5" />
                  Tous les dossiers ({cabinetSummary.totalDossiers})
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={driftFilter === "CRITICAL" ? "destructive" : "outline"}
                  onClick={() => setDriftFilter("CRITICAL")}
                  className="gap-1.5 text-xs h-8"
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Dérives critiques ({cabinetSummary.criticalDriftCount})
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={driftFilter === "MODERATE" ? "secondary" : "outline"}
                  onClick={() => setDriftFilter("MODERATE")}
                  className={`gap-1.5 text-xs h-8 ${driftFilter === "MODERATE" ? "bg-warning-600 text-white hover:bg-warning-700" : ""}`}
                >
                  <AlertCircle className="w-3.5 h-3.5" />
                  Dérives modérées ({cabinetSummary.moderateDriftCount})
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={driftFilter === "OVER_LIQUID" ? "secondary" : "outline"}
                  onClick={() => setDriftFilter("OVER_LIQUID")}
                  className={`gap-1.5 text-xs h-8 ${driftFilter === "OVER_LIQUID" ? "bg-info-600 text-white hover:bg-info-700" : ""}`}
                >
                  <Droplets className="w-3.5 h-3.5" />
                  Surliquidité ({cabinetSummary.overLiquidCount})
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={driftFilter === "ALIGNED" ? "secondary" : "outline"}
                  onClick={() => setDriftFilter("ALIGNED")}
                  className={`gap-1.5 text-xs h-8 ${driftFilter === "ALIGNED" ? "bg-success-600 text-white hover:bg-success-700" : ""}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Alignés ({cabinetSummary.alignedCount})
                </Button>
              </div>

              {/* Tableau de la Matrice */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-workspace text-subtle uppercase tracking-wider text-[11px] font-bold">
                      <th className="py-3 px-4">Dossier / Client</th>
                      <th className="py-3 px-4">AUM Net</th>
                      <th className="py-3 px-4">Profil Cible</th>
                      <th className="py-3 px-4">Répartition Actuelle (L / P / I / A)</th>
                      <th className="py-3 px-4">Dérive Max</th>
                      <th className="py-3 px-4">Signaux</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-workspace">
                    {filteredDossiers.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="py-12 text-center text-subtle">
                          Aucun dossier ne correspond aux critères sélectionnés.
                        </td>
                      </tr>
                    ) : (
                      filteredDossiers.map(dossier => {
                        const isCritical = dossier.status === "CRITICAL_DRIFT";
                        const isModerate = dossier.status === "MODERATE_DRIFT";
                        const isAligned = dossier.status === "ALIGNED";

                        return (
                          <tr
                            key={dossier.id}
                            className="hover:bg-surface-muted/40 transition-colors group"
                          >
                            <td className="py-3.5 px-4 font-semibold text-primary">
                              <div className="flex items-center gap-2.5">
                                <span className="p-1.5 rounded-lg bg-surface-muted text-subtle shrink-0">
                                  {dossier.type === "societe" ? (
                                    <Building2 className="w-4 h-4 text-secondary" />
                                  ) : (
                                    <Users className="w-4 h-4 text-primary" />
                                  )}
                                </span>
                                <div>
                                  <div className="font-bold text-primary">{dossier.name}</div>
                                  {dossier.reference && (
                                    <div className="text-[10px] text-subtle font-mono">{dossier.reference}</div>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td className="py-3.5 px-4 font-bold text-primary">
                              {formatEuro(dossier.aum)}
                            </td>

                            <td className="py-3.5 px-4">
                              <Badge variant="outline" className="text-[11px] font-semibold">
                                {dossier.profilRisque}
                              </Badge>
                            </td>

                            <td className="py-3.5 px-4">
                              <div className="flex items-center gap-1 font-mono text-[11px]">
                                <span className="text-info-600 font-bold">{dossier.currentPercentages.liquidites}%</span>
                                <span className="text-subtle">/</span>
                                <span className="text-success-600 font-bold">{dossier.currentPercentages.placements}%</span>
                                <span className="text-subtle">/</span>
                                <span className="text-warning-600 font-bold">{dossier.currentPercentages.immobilier}%</span>
                                <span className="text-subtle">/</span>
                                <span className="text-purple-600 font-bold">{dossier.currentPercentages.alternatif}%</span>
                              </div>
                            </td>

                            <td className="py-3.5 px-4">
                              <Badge
                                variant={isCritical ? "destructive" : isModerate ? "warning" : "success"}
                                className="text-[11px] font-bold gap-1"
                              >
                                {isCritical && <AlertTriangle className="w-3 h-3" />}
                                {isModerate && <AlertCircle className="w-3 h-3" />}
                                {isAligned && <CheckCircle2 className="w-3 h-3" />}
                                ±{dossier.maxDrift}%
                              </Badge>
                            </td>

                            <td className="py-3.5 px-4">
                              <div className="flex flex-wrap gap-1">
                                {dossier.tags.map(tag => (
                                  <Badge
                                    key={tag.code}
                                    variant="secondary"
                                    className="text-[10px] font-medium py-0 px-2"
                                  >
                                    {tag.label}
                                  </Badge>
                                ))}
                                {dossier.tags.length === 0 && (
                                  <span className="text-[11px] text-subtle">—</span>
                                )}
                              </div>
                            </td>

                            <td className="py-3.5 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedClientType(dossier.type);
                                    setSelectedClientId(dossier.id);
                                  }}
                                  className="text-xs h-7 px-2.5 gap-1"
                                >
                                  Auditer
                                </Button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleSimulerArbitrage(dossier)}
                                  className="text-xs h-7 px-2.5 gap-1 shadow-hp-xs"
                                >
                                  <Calculator className="w-3.5 h-3.5" />
                                  Arbitrer
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          /* ========================================================= */
          /* MODE 2 : VUE DIAGNOSTIC DOSSIER INDIVIDUEL               */
          /* ========================================================= */
          clientDetailLoading ? (
            <div className="flex h-64 items-center justify-center">
              <MiniLoader />
            </div>
          ) : !clientAnalysis ? (
            <div className="workspace-card p-12 text-center">
              <p className="text-subtle">Données introuvables pour ce client.</p>
              <Button variant="outline" size="sm" onClick={() => setSelectedClientId("")} className="mt-4">
                Revenir à la vue cabinet
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Synthèse Haute du Dossier Client */}
              <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold uppercase tracking-wider text-secondary flex items-center gap-1">
                        {selectedClientType === "societe" ? <Building2 className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
                        {selectedClientType === "societe" ? "Dossier Personne Morale" : "Dossier Foyer Fiscal"}
                      </span>
                    </div>
                    <h2 className="text-2xl font-black text-primary tracking-tight">
                      {selectedClientData.client?.denomination || selectedClientData.client?.nom || selectedClientData.client?.nomFamille}
                    </h2>
                    <p className="text-xs text-subtle mt-1 flex items-center gap-2">
                      <span>Profil contractuel : <strong>{clientAnalysis.assets.profilRisque}</strong></span>
                      <span>•</span>
                      <span>Horizon : <strong>{clientAnalysis.assets.horizonAnnees} ans</strong></span>
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="p-3 bg-surface-muted/50 rounded-xl border border-workspace">
                      <span className="block text-[10px] uppercase font-bold text-subtle">AUM Net</span>
                      <span className="text-base font-black text-primary">{formatEuro(clientAnalysis.summary.patrimoineNet)}</span>
                    </div>

                    <div className="p-3 bg-surface-muted/50 rounded-xl border border-workspace">
                      <span className="block text-[10px] uppercase font-bold text-subtle">Dérive Max vs Cible</span>
                      <span className={`text-base font-black ${
                        clientAnalysis.drift.status === "CRITICAL_DRIFT" ? "text-danger" : clientAnalysis.drift.status === "MODERATE_DRIFT" ? "text-warning" : "text-success"
                      }`}>
                        ±{clientAnalysis.drift.maxDrift}%
                      </span>
                    </div>

                    <Button
                      variant="primary"
                      onClick={handleSimulerArbitrageFromDetail}
                      className="gap-2 text-xs py-2.5 px-4 shadow-hp-sm"
                    >
                      <Calculator className="w-4 h-4" />
                      Lancer la simulation d'arbitrage
                    </Button>
                  </div>
                </div>
              </div>

              {/* Analyse Graphique & Dérive */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Colonne Gauche : Répartition & Dérive */}
                <div className="lg:col-span-7 flex flex-col gap-6">
                  {/* Pie Chart Actuel */}
                  <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
                    <h3 className="text-base font-bold text-primary mb-4 flex items-center justify-between">
                      <span>Répartition Actuelle Constatée</span>
                      <span className="text-xs font-normal text-subtle">Données réelles en base</span>
                    </h3>
                    <div style={{ width: '100%', height: 280 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={clientAnalysis.chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={105}
                            paddingAngle={4}
                            dataKey="value"
                            stroke="none"
                          >
                            {clientAnalysis.chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={ASSET_COLORS[entry.name]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatEuro(value)} />
                          <Legend verticalAlign="bottom" height={36} iconType="circle" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Drift Analysis Bar Chart */}
                  <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
                    <h3 className="text-base font-bold text-primary mb-1">
                      Analyse d'Écart vs Profil Cible ({clientAnalysis.assets.profilRisque})
                    </h3>
                    <p className="text-xs text-subtle mb-4">
                      Comparaison directe entre la répartition réelle et la clé d'allocation cible contractuelle.
                    </p>
                    <div style={{ width: '100%', height: 240 }}>
                      <ResponsiveContainer>
                        <BarChart data={clientAnalysis.driftBarData} margin={{ top: 15, right: 20, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--hp-color-border-muted)" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--hp-color-text-subtle)' }} />
                          <YAxis unit="%" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--hp-color-text-subtle)' }} />
                          <Tooltip formatter={(value) => `${value}%`} />
                          <Legend iconType="circle" />
                          <Bar dataKey="Actuel" fill="var(--hp-info-600)" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Cible" fill="var(--hp-bronze-300)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Colonne Droite : Poches détaillées & Diagnostic de gouvernance */}
                <div className="lg:col-span-5 flex flex-col gap-6">
                  {/* Jauge de Liquidité */}
                  <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
                    <h3 className="text-sm font-bold text-primary mb-3 flex items-center gap-2">
                      <Droplets className="w-4 h-4 text-info-600" />
                      Profil de Liquidité Immédiate
                    </h3>
                    <div className="w-full bg-surface-muted rounded-full h-3 mb-2 overflow-hidden">
                      <div
                        className="bg-info-500 h-3 rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(100, clientAnalysis.drift.liquidityRatio)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs font-semibold">
                      <span className="text-subtle">Illiquide</span>
                      <span className="text-info-600">{clientAnalysis.drift.liquidityRatio}% Actifs Disponibles</span>
                    </div>
                  </div>

                  {/* Détail des 4 Poches */}
                  <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
                    <h3 className="text-sm font-bold text-primary mb-4 border-b border-workspace pb-2">
                      Détail des Poches & Écarts (€ et %)
                    </h3>
                    <div className="space-y-4">
                      {clientAnalysis.driftBarData.map(poche => {
                        const montant = clientAnalysis.initialAllocation[poche.name];
                        const drift = poche.Drift;
                        const isPositive = drift > 0;

                        return (
                          <div key={poche.name} className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <span
                                className="w-3 h-3 rounded-full shrink-0"
                                style={{ backgroundColor: ASSET_COLORS[poche.name] }}
                              />
                              <div>
                                <span className="text-sm font-semibold text-primary">{poche.name}</span>
                                <div className="flex items-center gap-1 text-[11px]">
                                  {isPositive ? (
                                    <ArrowUpRight className="w-3 h-3 text-danger" />
                                  ) : (
                                    <ArrowDownRight className="w-3 h-3 text-warning" />
                                  )}
                                  <span className="text-subtle font-mono">
                                    Écart : {isPositive ? '+' : ''}{drift}%
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-sm font-bold text-primary">{formatEuro(montant)}</span>
                              <span className="block text-[10px] text-subtle">{poche.Actuel}% du brut</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Bloc Passerelle Simulateur */}
                  <div className="workspace-card p-6 bg-gradient-to-br from-secondary/5 to-secondary/15 border border-secondary/20 rounded-2xl text-center">
                    <Calculator className="w-8 h-8 text-secondary mx-auto mb-2" />
                    <h4 className="text-base font-bold text-primary mb-1">
                      Besoin d'ajuster cette allocation ?
                    </h4>
                    <p className="text-xs text-subtle mb-4">
                      Ouvrez le simulateur d'arbitrage Hipparchus pour tester des réallocations vers les Club Deals et fonds de dette privée.
                    </p>
                    <Button
                      variant="primary"
                      onClick={handleSimulerArbitrageFromDetail}
                      className="w-full gap-2 text-xs py-2.5 justify-center"
                    >
                      <Calculator className="w-4 h-4" />
                      Simuler un arbitrage pour ce dossier
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </PageAnimator>
    </div>
  );
}
